import { useId, useState, type ReactNode } from "react";
import styles from "./BarChart.module.css";
import { Money } from "./Money.js";

export interface Bar {
  id: string;
  label: string;
  pence: number;
  /** A chart token name (`chart-1` … `chart-8`), stored on the category. */
  colour?: string | null;
  onSelect?: () => void;
}

interface BarChartProps {
  bars: Bar[];
  emptyLabel?: string;
  /**
   * Turns every bar into a disclosure: what is behind it opens underneath it,
   * one row at a time. Called only for the row that is open, so a caller that
   * fetches a bar's rows fetches them when somebody asks for them.
   */
  renderDetails?: (bar: Bar) => ReactNode;
}

const CHART_TOKENS = [
  "chart-1",
  "chart-2",
  "chart-3",
  "chart-4",
  "chart-5",
  "chart-6",
  "chart-7",
  "chart-8",
];

/**
 * A horizontal bar breakdown, which on a phone is the readable form of a pie
 * and is also the table behind it — the label, the figure and the proportion
 * are all present, so there is no separate accessible version to keep in step.
 *
 * Colour never carries meaning on its own here: every bar is labelled and
 * every figure is written out.
 */
export function BarChart({ bars, emptyLabel = "Nothing yet", renderDetails }: BarChartProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const panelPrefix = useId();

  if (bars.length === 0) {
    return (
      <p className={styles.empty} role="status">
        {emptyLabel}
      </p>
    );
  }

  const rows = [...bars].sort((a, b) => Math.abs(b.pence) - Math.abs(a.pence));
  const largest = Math.max(...rows.map((row) => Math.abs(row.pence)), 1);

  return (
    <ul className={styles.chart}>
      {rows.map((row, index) => {
        const width = `${String(Math.round((Math.abs(row.pence) / largest) * 100))}%`;
        const token = row.colour ?? CHART_TOKENS[index % CHART_TOKENS.length];
        const content = (
          <>
            <span className={styles.label}>{row.label}</span>
            <Money pence={row.pence} tone="none" className={styles.value} />
            <span className={styles.track}>
              <span
                className={styles.bar}
                style={{ width, background: `var(--${token ?? "chart-8"})` }}
              />
            </span>
          </>
        );

        const expandable = renderDetails !== undefined;
        const open = expandable && openId === row.id;
        const panelId = `${panelPrefix}-${row.id}`;

        return (
          <li key={row.id} className={styles.row}>
            {expandable || row.onSelect ? (
              <button
                type="button"
                className={styles.hit}
                onClick={() => {
                  if (expandable) setOpenId(open ? null : row.id);
                  row.onSelect?.();
                }}
                aria-expanded={expandable ? open : undefined}
                // Only while it is there to point at.
                aria-controls={open ? panelId : undefined}
              >
                {content}
              </button>
            ) : (
              <span className={styles.hit}>{content}</span>
            )}
            {renderDetails && openId === row.id && (
              <div id={panelId} className={styles.panel}>
                {renderDetails(row)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
